import React, { useLayoutEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { Stack, Typography } from "@mui/material";
import { Lock, RefreshCw, Upload } from "lucide-react";

import LayerdIcon from "../../../common/LayerdIcon";
import V2PageHeader from "../../../common/V2PageHeader";
import SiteScopeGate from "../../../common/SiteScopeGate";
import { GhostButton, PrimaryButton } from "../../../common/Button";
import { ReactComponent as LoadingIcon } from "../../../../vendor/_new/icon/loading.svg";
import colors from "../../../../utils/colors";
import useSite from "../../../../hooks/user/useSite";
import useSSLConfig from "../../../../hooks/user/useSSLConfig";
import useAuth from "../../../../hooks/useAuth";
import useHome from "../../../../hooks/user/useHome";
import useGlobalSite from "../../../../hooks/user/useGlobalSite";
import {
  isAllSites,
  LAST_SINGLE_SITE_STORAGE_KEY,
  pickDefaultSiteId,
} from "../../../../utils/globalSite";
import SSLTLSPage from "./SSLTLSPage";
import { certsBodySecondarySx } from "./certsTypography";
import { CERTS_PAGE_SHELL_SPACING } from "./certsLayout";
// import { UnsavedChangesConfirmModal } from "../super/design";

export default function SSLTlsConfigurationPage() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { siteList, getSitesForItems, selectSite } = useSite();
  const { getSSLConfig } = useSSLConfig();
  const { applySiteSelectorUi } = useHome();
  const { curSiteId, curSiteUid, siteOptions } = useGlobalSite();
  const [refreshing, setRefreshing] = useState(false);
  const [installTrigger, setInstallTrigger] = useState(0);
  // const [installWarnOpen, setInstallWarnOpen] = useState(false);

  const urlSite = new URLSearchParams(location.search).get("site_uid");
  const siteUid = curSiteUid;

  const selectedSite = React.useMemo(
    () => (siteUid && siteList ? siteList.find((s) => s.id === siteUid) ?? null : null),
    [siteList, siteUid]
  );

  useLayoutEffect(() => {
    let nextSiteId = curSiteId;
    if (!isAllSites(curSiteId)) {
      try {
        sessionStorage.setItem(LAST_SINGLE_SITE_STORAGE_KEY, curSiteId);
      } catch {
        // ignore
      }
    } else if (siteOptions.length > 0) {
      nextSiteId = pickDefaultSiteId(siteOptions) ?? siteOptions[0].site_id;
    }

    applySiteSelectorUi({
      enabled: true,
      showAllOption: false,
      curSiteId: isAllSites(nextSiteId) ? undefined : nextSiteId,
    });
  }, [curSiteId, siteOptions, applySiteSelectorUi]);

  React.useEffect(() => {
    if (isAuthenticated && !siteList) getSitesForItems();
  }, [isAuthenticated, siteList, getSitesForItems]);

  React.useEffect(() => {
    if (!urlSite || !siteList?.length) return;
    const match = siteList.find((s) => s.id === urlSite);
    if (!match?.site_id) return;
    try {
      sessionStorage.setItem(LAST_SINGLE_SITE_STORAGE_KEY, match.site_id);
    } catch {
      // ignore
    }
    applySiteSelectorUi({
      enabled: true,
      showAllOption: false,
      curSiteId: match.site_id,
    });
  }, [urlSite, siteList, applySiteSelectorUi]);

  React.useEffect(() => {
    if (siteUid) {
      getSSLConfig?.(siteUid, true);
      if (selectedSite?.site_id) selectSite?.(selectedSite.site_id);
    }
  }, [siteUid, selectedSite, getSSLConfig, selectSite]);

  const handleInstallConsumed = React.useCallback(() => {
    setInstallTrigger(0);
  }, []);

  const handleInstallClick = React.useCallback(() => {
    // setInstallWarnOpen(true);
    setInstallTrigger((n) => n + 1);
  }, []);

  // const handleInstallWarnCancel = React.useCallback(() => {
  //   setInstallWarnOpen(false);
  // }, []);

  // const handleInstallWarnContinue = React.useCallback(() => {
  //   setInstallWarnOpen(false);
  //   setInstallTrigger((n) => n + 1);
  // }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      if (siteUid) await getSSLConfig?.(siteUid, true);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <>
      <Helmet title="SSL / TLS · Configuration" />
      <Stack
        spacing={CERTS_PAGE_SHELL_SPACING}
        sx={{ p: 4, fontSize: 14, lineHeight: "21px", color: colors.neutral[1000] }}
      >
        <V2PageHeader
          icon={Lock}
          title="SSL / TLS Configuration"
          description="Manage the certificate, encryption mode and TLS settings used to serve this site."
          helpTooltip="Manage the edge certificate, encryption mode between visitors and origin, TLS protocol settings, redirects, and HSTS for the selected site."
          sx={{ pb: 0 }}
          actions={(
            <Stack direction="row" spacing={1}>
              <GhostButton
                disabled={refreshing}
                startIcon={
                  refreshing ? (
                    <LoadingIcon style={{ color: colors.neutral[600], width: 16, height: 16 }} />
                  ) : (
                    <LayerdIcon icon={RefreshCw} size="sm" color="neutral" />
                  )
                }
                onClick={handleRefresh}
              >
                Refresh
              </GhostButton>
              <PrimaryButton
                startIcon={<LayerdIcon icon={Upload} size="sm" color="inherit" />}
                onClick={handleInstallClick}
              >
                Install certificate
              </PrimaryButton>
            </Stack>
          )}
        />

        <SiteScopeGate mode="single" autoPickSite={false}>
          {(siteUid) => {
            const site = siteList?.find((s) => s.id === siteUid) ?? null;
            return (
              <>
                {site && (
                  <Typography sx={{ ...certsBodySecondarySx }}>
                    <b>{site.site_id}</b>
                  </Typography>
                )}
                <SSLTLSPage
                  siteUid={siteUid}
                  installTrigger={installTrigger}
                  onInstallConsumed={handleInstallConsumed}
                />
              </>
            );
          }}
        </SiteScopeGate>
      </Stack>

      {/*
      <UnsavedChangesConfirmModal
        open={installWarnOpen}
        onCancel={handleInstallWarnCancel}
        onContinue={handleInstallWarnContinue}
        title="Install a new certificate?"
        body="Starting a new managed certificate keeps your current active certificate serving until the new one is ready. If you cancel before validation completes, only the in-progress order is removed — your active certificate is not affected."
        cancelLabel="Cancel"
        continueLabel="Continue"
      />
      */}
    </>
  );
}
